import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'src/app/store';

import { fetchHistories } from '../services/ticket.service';
import { History } from '../types';

interface LoadHistoriesProps {
  id: string;
}

interface HistoriesState {
  error: string | undefined;
  isHistoriesLoading: boolean;
  histories: History[] | undefined;
  activeRequestId: string | null;
}

const initialState: HistoriesState = {
  isHistoriesLoading: false,
  histories: undefined,
  error: undefined,
  activeRequestId: null
};

export const loadHistories = createAsyncThunk<
  History[],
  LoadHistoriesProps,
  { rejectValue: Error; requestId: string }
>('history/loadHistories', async (params, { rejectWithValue, getState, requestId }) => {
  const { id } = params;
  try {
    const response = await fetchHistories(id);
    const { activeRequestId } = (getState() as RootState).history;
    if (requestId !== activeRequestId) {
      return rejectWithValue(new Error('Request ignored'));
    }
    return response.data;
  } catch (e) {
    const error = e as Error;
    if (!error.message) {
      throw error;
    }
    return rejectWithValue(error);
  }
});

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadHistories.pending, (state, action) => {
        state.isHistoriesLoading = true;
        state.histories = undefined;
        state.error = undefined;
        state.activeRequestId = action.meta.requestId;
      })
      .addCase(loadHistories.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId === state.activeRequestId) {
          state.isHistoriesLoading = false;
          state.histories = payload || [];
          state.activeRequestId = null;
          state.error = undefined;
        }
      })
      .addCase(loadHistories.rejected, (state, { error, meta }) => {
        if (meta.requestId === state.activeRequestId) {
          state.isHistoriesLoading = false;
          state.error = error.message;
          state.histories = undefined;
          state.activeRequestId = null;
        }
      });
  }
});

export default historySlice.reducer;
